import { motion } from "framer-motion";
import {
  RiArrowRightLine,
  RiBriefcaseFill,
  RiChatQuoteLine,
  RiExternalLinkLine,
  RiHeart2Fill,
  RiLightbulbFlashFill,
  RiMapPinFill,
  RiRocket2Fill,
  RiTeamFill,
} from "@remixicon/react";
import { Link } from "react-router-dom";

import { ColorBand, ColorBandInner } from "@/components/landing/ColorBand";
import {
  fadeIn,
  fadeUp,
  inView,
  staggerContainer,
  staggerItem,
} from "@/components/landing/marketing-motion";
import { Button } from "@/components/ui/button";
import { APP_REGISTER_URL } from "@/lib/site";

const values = [
  {
    title: "Simplicity first",
    body: "We remove technical barriers so everyday entrepreneurs can launch fast.",
    Icon: RiLightbulbFlashFill,
    tone: "bg-[#d4c4fc]",
  },
  {
    title: "Operators first",
    body: "We design for people who answer DMs at midnight and pack orders before breakfast.",
    Icon: RiTeamFill,
    tone: "bg-[#efe04a]",
  },
  {
    title: "Momentum",
    body: "Small wins compound: a clear link today, calmer fulfillment tomorrow, smarter restocks next week.",
    Icon: RiRocket2Fill,
    tone: "bg-[#d2e4c4]",
  },
  {
    title: "Care in the craft",
    body: "Polish matters. Trust in software shows up in the details merchants and shoppers feel.",
    Icon: RiHeart2Fill,
    tone: "bg-white",
  },
] as const;

const storyImages = [
  "https://res.cloudinary.com/da4c8kuqv/image/upload/v1774167335/about-1_qjceyh.jpg",
  "https://res.cloudinary.com/da4c8kuqv/image/upload/v1774167335/about-2_jxhqd8.jpg",
  "https://res.cloudinary.com/da4c8kuqv/image/upload/v1774167335/about-3_zwriek.jpg",
] as const;

export function CompanyPage() {
  return (
    <div>
      {/* Founders-style hero: copy + collage */}
      <ColorBand className="bg-[#f6f4ef] pt-28 text-black sm:pt-32 lg:pt-36">
        <div
          className="pointer-events-none absolute -right-20 top-10 size-80 rounded-full bg-primary/10 blur-3xl"
          aria-hidden
        />
        <ColorBandInner>
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div {...fadeUp} className="max-w-xl">
              <h1 className="font-display mt-4 text-4xl leading-[0.92] sm:text-5xl lg:text-[3.4rem] xl:text-[4.5rem]">
                Our Founder&rsquo;s Story
              </h1>
              <p className="mt-6 text-base font-semibold leading-relaxed text-black xl:text-lg">
                Sqaleshop is a tool to help you digitize your business and grow
                your sales, in one simple link - making your business more
                visible and accessible to your customers. Here's our story.
              </p>
            </motion.div>
            <motion.div {...fadeIn} className="grid grid-cols-2 gap-3 sm:gap-4">
              {storyImages.map((src, i) => (
                <div
                  key={src}
                  className={
                    i % 2 === 1
                      ? "mt-6 overflow-hidden rounded-[1.75rem] sm:mt-10 sm:rounded-[2rem]"
                      : "overflow-hidden rounded-[1.75rem] sm:rounded-[2rem]"
                  }
                >
                  <img
                    src={src}
                    alt=""
                    className="aspect-[4/3] w-full object-cover"
                  />
                </div>
              ))}
            </motion.div>
          </div>
        </ColorBandInner>
      </ColorBand>

      {/* Centered prose */}
      <ColorBand className="bg-[#f6f4ef] text-black">
        <ColorBandInner className="max-w-[80%]">
          <motion.div
            {...fadeUp}
            className="space-y-6 text-base font-semibold leading-relaxed text-black xl:text-xl"
          >
            <p>
              Sqaleshop was founded by software engineer and entrepreneur Imam
              Abubakar, after repeatedly seeing how difficult it was for small
              businesses to turn their social media presence into a proper sales
              process.
            </p>
            <p>
              Many businesses were already finding customers through Instagram,
              TikTok and WhatsApp, but selling still meant answering the same
              questions in DMs, sending prices manually, confirming payments
              through screenshots and keeping track of orders across different
              conversations.
            </p>
            <p>Imam saw an opportunity to make that process much simpler.</p>
            <p>
              Sqaleshop was created to give businesses one place to showcase
              what they sell, receive and manage orders, understand their
              customers and make it easier for people discovering them online to
              actually buy.
            </p>
            <p>
              Today, Sqaleshop is being built as a simple commerce platform for
              modern businesses, particularly social sellers who do not need the
              complexity of a traditional ecommerce website but need something
              more powerful than selling entirely through DMs.
            </p>
            <p>
              Built by Sqaleup Inc., the company founded by Imam Abubakar,
              Sqaleshop is working towards a simple goal: help businesses turn
              online attention into actual customers.
            </p>
          </motion.div>
        </ColorBandInner>
      </ColorBand>

      {/* Mission band */}
      <ColorBand className="bg-primary text-white">
        <div
          className="pointer-events-none absolute -right-20 top-0 size-96 rounded-full bg-[#be9afc]/25 blur-3xl"
          aria-hidden
        />
        <ColorBandInner className="max-w-3xl text-center lg:max-w-3xl">
          <motion.p
            {...fadeUp}
            className="font-heading text-sm font-semibold uppercase tracking-[0.18em] text-white/70"
          >
            Mission
          </motion.p>
          <motion.h2
            {...fadeUp}
            className="font-display mt-4 text-3xl leading-[0.92] sm:text-4xl lg:text-[3rem]"
          >
            Give every small seller a professional storefront and operational
            clarity.
          </motion.h2>
        </ColorBandInner>
      </ColorBand>

      {/* Quote */}
      <ColorBand className="bg-[#be9afc] text-primary">
        <ColorBandInner className="max-w-3xl text-center lg:max-w-3xl">
          <RiChatQuoteLine
            className="mx-auto size-12 text-primary/30 sm:size-14"
            aria-hidden
          />
          <motion.blockquote
            {...fadeUp}
            className="font-display mt-6 text-2xl leading-snug sm:text-3xl lg:text-[2.35rem] lg:leading-[1.15]"
          >
            Anyone can build software. What matters is building something people
            actually use, pay for, and trust.
          </motion.blockquote>
          <p className="mt-6 text-sm font-semibold text-primary/70">
            Imam Abubakar · Founder, Sqaleup Inc.
          </p>
        </ColorBandInner>
      </ColorBand>

      {/* Values */}
      <ColorBand className="bg-[#f6f4ef] text-primary">
        <ColorBandInner>
          <motion.h2
            {...fadeUp}
            className="font-display max-w-3xl text-3xl leading-[0.92] sm:text-4xl lg:text-[3rem]"
          >
            How we build
          </motion.h2>
          <p className="mt-4 max-w-xl text-base font-semibold text-primary/65">
            Clarity before code, execution before hype.
          </p>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={inView}
            className="mt-12 grid gap-5 sm:grid-cols-2"
          >
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                variants={staggerItem}
                className={`rounded-[2.5rem] p-7 sm:p-8 ${v.tone}`}
              >
                <span className="font-display text-5xl leading-none text-primary/20">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="mt-4 flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <v.Icon className="size-6" />
                </span>
                <h3 className="font-heading mt-5 text-xl text-primary">
                  {v.title}
                </h3>
                <p className="mt-2 text-sm font-semibold leading-relaxed text-primary/65">
                  {v.body}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </ColorBandInner>
      </ColorBand>

      {/* HQ + links */}
      <ColorBand className="bg-white text-primary">
        <ColorBandInner>
          <motion.div
            {...fadeIn}
            className="flex flex-col gap-6 rounded-[2.5rem] bg-[#f6f4ef] p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10"
          >
            <div className="flex items-start gap-4">
              <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <RiMapPinFill className="size-6" />
              </span>
              <div>
                <p className="font-heading text-lg text-primary">
                  Headquarters
                </p>
                <p className="mt-2 text-sm font-semibold leading-relaxed text-primary/65">
                  Sqaleup Inc. is based in Luton, United Kingdom. We work with
                  founders and teams worldwide.
                </p>
              </div>
            </div>
            <a
              href="https://sqaleupinc.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-primary shadow-sm transition-colors hover:bg-primary hover:text-white"
            >
              Studio at sqaleupinc.com
              <RiExternalLinkLine className="size-4" />
            </a>
          </motion.div>

          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            <motion.div
              {...fadeIn}
              className="rounded-[2.5rem] bg-[#d4c4fc] p-8"
            >
              <span className="flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <RiTeamFill className="size-6" />
              </span>
              <p className="font-heading mt-5 text-xl text-primary">People</p>
              <p className="mt-2 text-sm font-semibold text-primary/65">
                Meet the team and see how we work together.
              </p>
              <Button
                className="mt-6 rounded-full gap-1.5 font-semibold"
                asChild
              >
                <Link to="/team">
                  Team
                  <RiArrowRightLine className="size-4" />
                </Link>
              </Button>
            </motion.div>
            <motion.div
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.06 }}
              className="rounded-[2.5rem] bg-[#efe04a] p-8"
            >
              <span className="flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <RiBriefcaseFill className="size-6" />
              </span>
              <p className="font-heading mt-5 text-xl text-primary">Careers</p>
              <p className="mt-2 text-sm font-semibold text-primary/65">
                We hire merchant-obsessed builders and communicators.
              </p>
              <Button
                className="mt-6 rounded-full gap-1.5 font-semibold"
                asChild
              >
                <Link to="/careers">
                  Careers
                  <RiArrowRightLine className="size-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </ColorBandInner>
      </ColorBand>

      <ColorBand className="bg-[#1a1a6e] text-white">
        <ColorBandInner className="text-center">
          <motion.h2
            {...fadeUp}
            className="font-display text-3xl leading-[0.92] sm:text-4xl lg:text-[3rem]"
          >
            Work with Sqaleup
          </motion.h2>
          <p className="mx-auto mt-4 max-w-lg text-base font-semibold text-white/80">
            Partnerships, press, or enterprise conversations. We read every
            note.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button
              size="xl"
              className="rounded-full bg-[#b6a2ff] px-8 font-semibold text-primary shadow-none hover:bg-[#b6a2ff]"
              asChild
            >
              <Link to="/contact" className="gap-2">
                Contact us
                <RiArrowRightLine className="size-4" />
              </Link>
            </Button>
          
          </div>
        </ColorBandInner>
      </ColorBand>
    </div>
  );
}
